/**
 * The built-in admin panel.
 *
 * A route in the game client that talks to the admin API the backend already
 * exposes, so there is one login and one place where "who may do this" is
 * decided (RolesGuard, server-side).
 *
 * ─── It is mounted at /admin-panel, not /admin ──────────────────────────────
 * Caddy redirects `/admin` to `/admin-panel` (308). This panel lives at
 * `/admin-panel`, which falls through to the SPA.
 *
 * ─── Shape ──────────────────────────────────────────────────────────────────
 * A rail of tabs on the left, one tab mounted at a time on the right, and a
 * notice bar between the two that outlives whatever the tab does to its own
 * body. Each tab is an `AdminTab` (see admin-kit.ts) that fetches on mount and
 * returns a teardown; the shell knows nothing about any of them beyond that.
 *
 * ─── Why the tab lives in the hash ──────────────────────────────────────────
 * `#users`, `#meta`, and so on. Not sub-routes, because main.ts's router
 * matches on `location.pathname` and adding twelve paths to it would put the
 * panel's internal structure into the application's route table. The hash is
 * read on mount and written on every switch, so a reload or a shared link comes
 * back to the same tab, and the browser's own back button walks the tabs.
 *
 * ─── Access ─────────────────────────────────────────────────────────────────
 * main.ts refuses the route to anyone `canOpenAdmin()` rejects, and every
 * endpoint behind it is guarded again on the server. The client-side check is
 * there so a non-admin sees the game rather than a panel full of 403s — it is
 * not the thing keeping anybody out.
 */
import { authStore } from '../../core/state/auth-store';
import { canOpenAdmin } from '../state/account-state';
import ADMIN_CSS from './admin.css';
import type { AdminTab, TabContext } from './admin-kit';
import { esc } from './admin-kit';

import { dashboardTab } from './tabs/dashboard-tab';
import { usersTab } from './tabs/users-tab';
import { kycTab } from './tabs/kyc-tab';
import { withdrawalsTab } from './tabs/withdrawals-tab';
import { invoicesTab } from './tabs/invoices-tab';
import { newsTab } from './tabs/news-tab';
import { faqTab, legalTab, socialLinksTab } from './tabs/cms-tabs';
import { settingsTab } from './tabs/settings-tab';
import { metaTab } from './tabs/meta-tab';
import { apiKeysTab } from './tabs/api-keys-tab';
import { paymentsTab } from './tabs/payments-tab';

const STYLE_ID = 'tg-admin-styles';

/**
 * Tab order is the order of this list, and the rail groups by `group` in first
 * appearance order — so moving a tab is a one-line edit here and nowhere else.
 *
 * A group's tabs must stay CONTIGUOUS: the rail prints a heading whenever
 * `group` changes between adjacent entries, so splitting one prints its
 * heading twice and it reads as two different groups with the same name.
 * `admin-ui.test.ts` asserts this.
 */
export const ADMIN_TABS: AdminTab[] = [
  dashboardTab,
  usersTab,
  kycTab,
  paymentsTab,
  withdrawalsTab,
  invoicesTab,
  // CMS, in the order Janeks asked for: *"News, FAQ, Social links, Meta tags,
  // Legal pages, settings"* — everything an operator edits about the public
  // site, rather than split across a "Site" group and the rest.
  newsTab,
  faqTab,
  socialLinksTab,
  metaTab,
  legalTab,
  settingsTab,
  apiKeysTab,
];

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = ADMIN_CSS;
  document.head.appendChild(style);
}

/** The tab named by the URL hash, or the first one. */
export function tabFromHash(hash: string, tabs: AdminTab[] = ADMIN_TABS): AdminTab {
  const id = hash.replace(/^#/, '');
  return tabs.find((t) => t.id === id) ?? tabs[0];
}

export interface AdminOptions {
  navigate(path: string): void;
  /** Injectable for tests; defaults to the real tab list. */
  tabs?: AdminTab[];
}

/**
 * Mounts the panel into `host` and returns its teardown.
 *
 * The teardown takes the active tab down with it — every tab's own teardown
 * aborts its in-flight requests, so leaving the panel mid-load does not land a
 * response in a detached DOM.
 */
export function renderAdmin(host: HTMLElement, opts: AdminOptions): () => void {
  ensureStyles();

  const tabs = opts.tabs ?? ADMIN_TABS;
  const root = document.createElement('div');
  root.className = 'tg-adm';
  root.innerHTML = `
    <nav class="tg-adm-rail" aria-label="Admin sections">
      <p class="tg-adm-brand">Admin</p>
      ${railItems(tabs)}
      <div class="tg-adm-rail-spacer"></div>
      <a class="tg-adm-back" href="/play">&larr; Back to the game</a>
    </nav>
    <div class="tg-adm-main">
      <div class="tg-adm-notice" role="status"></div>
      <div class="tg-adm-pane"></div>
    </div>`;

  host.innerHTML = '';
  host.appendChild(root);

  const rail = root.querySelector<HTMLElement>('.tg-adm-rail')!;
  const pane = root.querySelector<HTMLElement>('.tg-adm-pane')!;
  const notice = root.querySelector<HTMLElement>('.tg-adm-notice')!;

  let active: AdminTab | null = null;
  let activeTeardown: (() => void) | null = null;
  let modalTeardown: (() => void) | null = null;

  const ctx: TabContext = {
    notify(message, kind = 'ok') {
      notice.className = `tg-adm-notice tg-adm-notice-${kind}`;
      notice.innerHTML = `<span>${esc(message)}</span>
        <button type="button" data-notice-dismiss aria-label="Dismiss">&times;</button>`;
    },
    confirm(options) {
      modalTeardown?.();
      const modal = document.createElement('div');
      modal.className = 'tg-adm-modal';
      modal.innerHTML = `
        <div class="tg-adm-modal-card" role="dialog" aria-modal="true">
          <h3>${esc(options.title)}</h3>
          <p>${esc(options.body)}</p>
          <div class="tg-adm-modal-acts">
            <button type="button" class="tg-adm-btn" data-modal="cancel">Cancel</button>
            <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-modal="ok">${esc(options.confirmLabel)}</button>
          </div>
        </div>`;
      const close = () => { modal.remove(); modalTeardown = null; };
      modal.addEventListener('click', (e) => {
        const action = (e.target as HTMLElement).closest<HTMLElement>('[data-modal]')?.dataset.modal;
        if (!action) return;
        close();
        if (action === 'ok') options.onConfirm();
      });
      document.body.appendChild(modal);
      modalTeardown = close;
    },
    prompt(options) {
      modalTeardown?.();
      const modal = document.createElement('div');
      modal.className = 'tg-adm-modal';
      modal.innerHTML = `
        <form class="tg-adm-modal-card" role="dialog" aria-modal="true">
          <h3>${esc(options.title)}</h3>
          <p>${esc(options.body)}</p>
          <label class="tg-adm-field">
            <span class="tg-adm-label">${esc(options.label)}</span>
            <textarea name="value" rows="3" required></textarea>
          </label>
          <div class="tg-adm-modal-acts" style="margin-top:14px">
            <button type="button" class="tg-adm-btn" data-modal="cancel">Cancel</button>
            <button type="submit" class="tg-adm-btn tg-adm-btn-danger">${esc(options.confirmLabel)}</button>
          </div>
        </form>`;
      const close = () => { modal.remove(); modalTeardown = null; };
      modal.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('[data-modal="cancel"]')) close();
      });
      modal.addEventListener('submit', (e) => {
        e.preventDefault();
        const value = modal.querySelector<HTMLTextAreaElement>('[name="value"]')!.value.trim();
        // `required` already blocks an empty submit in a real browser; the
        // guard is here because jsdom does not enforce constraint validation,
        // so a test would otherwise sail past it into a request the server
        // rejects.
        if (!value) return;
        close();
        options.onConfirm(value);
      });
      document.body.appendChild(modal);
      modalTeardown = close;
      modal.querySelector<HTMLTextAreaElement>('[name="value"]')?.focus();
    },
    navigate: opts.navigate,
  };

  function clearNotice(): void {
    notice.className = 'tg-adm-notice';
    notice.innerHTML = '';
  }

  function open(tab: AdminTab): void {
    if (active === tab) return;
    activeTeardown?.();
    activeTeardown = null;
    active = tab;
    clearNotice();
    pane.innerHTML = '';
    for (const b of rail.querySelectorAll<HTMLElement>('.tg-adm-tab')) {
      const on = b.dataset.admTab === tab.id;
      b.classList.toggle('tg-adm-tab-on', on);
      b.setAttribute('aria-current', String(on));
    }
    activeTeardown = tab.mount(pane, ctx);
  }

  const onRailClick = (e: MouseEvent) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>('[data-adm-tab]')?.dataset.admTab;
    if (!id) return;
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    // pushState, not a hash assignment: assigning `location.hash` also fires
    // hashchange, and the handler below would then re-open the tab that was
    // just opened, tearing down and refetching everything it had loaded.
    history.pushState(null, '', `${location.pathname}#${tab.id}`);
    open(tab);
  };
  rail.addEventListener('click', onRailClick);

  const onNoticeClick = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-notice-dismiss]')) clearNotice();
  };
  notice.addEventListener('click', onNoticeClick);

  // The back button walks the tabs, because each switch pushed a history entry.
  const onHashChange = () => open(tabFromHash(location.hash, tabs));
  window.addEventListener('popstate', onHashChange);

  open(tabFromHash(location.hash, tabs));

  return () => {
    window.removeEventListener('popstate', onHashChange);
    rail.removeEventListener('click', onRailClick);
    notice.removeEventListener('click', onNoticeClick);
    modalTeardown?.();
    activeTeardown?.();
    activeTeardown = null;
    active = null;
    root.remove();
  };
}

function railItems(tabs: AdminTab[]): string {
  const out: string[] = [];
  let group: string | null = null;
  for (const tab of tabs) {
    if (tab.group !== group) {
      group = tab.group;
      out.push(`<p class="tg-adm-group">${esc(group)}</p>`);
    }
    out.push(`<button type="button" class="tg-adm-tab" data-adm-tab="${esc(tab.id)}"
      aria-current="false">${esc(tab.label)}</button>`);
  }
  return out.join('');
}

/**
 * May the signed-in account open the panel right now?
 *
 * Reads the auth store rather than taking a user argument, so main.ts's route
 * guard and any future caller cannot disagree about which account is being
 * asked about.
 */
export function adminRouteAllowed(): boolean {
  const user = authStore.get().user as { role?: string } | null;
  return canOpenAdmin(user ? { role: user.role ?? '' } : null);
}
